import { useEffect, useMemo, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Modal } from '../../../shared/components/Modal';
import { formatBRL } from '../../../shared/lib/format';
import type { Produto } from '../../produtos/types';
import type { CartItem, ComplementoSelecionado } from '../types';

interface Props {
  open: boolean;
  produto: Produto | null;
  onClose: () => void;
  onConfirm: (item: CartItem) => void;
}

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export function ItemSelectorModal({ open, produto, onClose, onConfirm }: Props) {
  const [tamanhoId, setTamanhoId] = useState<string | null>(null);
  const [selecoes, setSelecoes] = useState<Record<string, string[]>>({});
  const [quantidade, setQuantidade] = useState(1);
  const [observacao, setObservacao] = useState('');

  useEffect(() => {
    if (!open || !produto) return;
    setTamanhoId(produto.tamanhos?.[0]?.id ?? null);
    setSelecoes({});
    setQuantidade(1);
    setObservacao('');
  }, [open, produto]);

  const tamanho = useMemo(
    () => produto?.tamanhos.find((t) => t.id === tamanhoId) ?? null,
    [produto, tamanhoId],
  );

  const precoBase = useMemo(() => {
    if (!produto) return 0;
    if (tamanho) return Number(tamanho.preco);
    return Number(produto.preco_unitario ?? 0);
  }, [produto, tamanho]);

  const complementosSelecionados = useMemo<ComplementoSelecionado[]>(() => {
    if (!produto) return [];
    const out: ComplementoSelecionado[] = [];
    for (const grupo of produto.grupos) {
      const ids = selecoes[grupo.id] ?? [];
      for (const cid of ids) {
        const c = grupo.complementos.find((x) => x.id === cid);
        if (c) {
          out.push({
            grupo_id: grupo.id,
            grupo_nome: grupo.nome,
            complemento_id: c.id,
            nome: c.nome,
            preco_adicional: Number(c.preco_adicional),
          });
        }
      }
    }
    return out;
  }, [produto, selecoes]);

  const complementosTotal = complementosSelecionados.reduce(
    (a, c) => a + c.preco_adicional,
    0,
  );
  const subtotal = (precoBase + complementosTotal) * quantidade;

  const toggle = (grupoId: string, compId: string, max: number) => {
    setSelecoes((prev) => {
      const cur = prev[grupoId] ?? [];
      if (cur.includes(compId)) {
        return { ...prev, [grupoId]: cur.filter((x) => x !== compId) };
      }
      if (cur.length >= max) {
        // substitui o primeiro se max=1, senao ignora
        if (max === 1) return { ...prev, [grupoId]: [compId] };
        return prev;
      }
      return { ...prev, [grupoId]: [...cur, compId] };
    });
  };

  const podeConfirmar = useMemo(() => {
    if (!produto) return false;
    for (const g of produto.grupos) {
      if (g.obrigatorio) {
        const n = (selecoes[g.id] ?? []).length;
        if (n < g.min_selecao) return false;
      }
    }
    return precoBase > 0 && quantidade > 0;
  }, [produto, selecoes, precoBase, quantidade]);

  const confirm = () => {
    if (!produto) return;
    onConfirm({
      uid: uid(),
      produto,
      tamanho_id: tamanhoId,
      tamanho_nome: tamanho?.nome ?? null,
      quantidade,
      preco_base: precoBase,
      complementos: complementosSelecionados,
      observacao: observacao.trim() || undefined,
    });
  };

  if (!produto) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={produto.nome}
      description={produto.descricao ?? undefined}
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn-primary" disabled={!podeConfirmar} onClick={confirm}>
            Adicionar {formatBRL(subtotal)}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {produto.tamanhos.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Tamanho</h3>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {produto.tamanhos.map((t) => {
                const active = tamanhoId === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTamanhoId(t.id)}
                    className={`rounded-lg border p-3 text-left text-sm transition ${
                      active
                        ? 'border-brand-600 bg-brand-50 text-brand-900'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <div className="font-medium">{t.nome}</div>
                    <div className="text-xs opacity-80">{formatBRL(Number(t.preco))}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {produto.grupos.map((grupo) => {
          const selecionados = selecoes[grupo.id] ?? [];
          const restante = grupo.max_selecao - selecionados.length;
          return (
            <div key={grupo.id}>
              <div className="mb-2 flex items-baseline justify-between">
                <h3 className="text-sm font-semibold text-slate-700">
                  {grupo.nome}
                  {grupo.obrigatorio && (
                    <span className="ml-1 text-xs font-normal text-red-600">* obrigatorio</span>
                  )}
                </h3>
                <span className="text-xs text-slate-500">
                  {grupo.min_selecao > 0 && `min ${grupo.min_selecao} • `}
                  max {grupo.max_selecao}
                  {restante <= 2 && restante > 0 && (
                    <span className="ml-1 text-brand-600">({restante} restante)</span>
                  )}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {grupo.complementos
                  .filter((c) => c.ativo)
                  .map((c) => {
                    const checked = selecionados.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggle(grupo.id, c.id, grupo.max_selecao)}
                        className={`rounded-full border px-3 py-1.5 text-xs transition ${
                          checked
                            ? 'border-brand-600 bg-brand-600 text-white'
                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {c.nome}
                        {Number(c.preco_adicional) > 0 && (
                          <span className="ml-1 opacity-80">
                            +{formatBRL(Number(c.preco_adicional))}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          );
        })}

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Observacao (opcional)</h3>
          <textarea
            className="input min-h-[60px]"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Ex: Sem canela"
            maxLength={500}
          />
        </div>

        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-3">
          <span className="text-sm font-semibold text-slate-700">Quantidade</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantidade((q) => Math.max(1, q - 1))}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="min-w-[2ch] text-center text-lg font-semibold">{quantidade}</span>
            <button
              type="button"
              onClick={() => setQuantidade((q) => q + 1)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
